<template>
  <div id="app-root" :data-theme="theme">
    <!-- Navigation — shown only when authenticated and not on admin routes -->
    <nav v-if="showNav" class="app-nav">
      <div class="app-nav-inner">
        <RouterLink :to="companyPath('/dashboard')" class="nav-brand">
          <img src="/logo.png" alt="" class="nav-logo" />
          {{ portalName }}
        </RouterLink>

        <button class="nav-toggle" @click="mobileNavOpen = !mobileNavOpen" aria-label="Toggle navigation">
          <span class="nav-toggle-bar" />
          <span class="nav-toggle-bar" />
          <span class="nav-toggle-bar" />
        </button>

        <div class="nav-links" :class="{ open: mobileNavOpen }">
          <RouterLink :to="companyPath('/dashboard')" class="nav-link" @click="mobileNavOpen = false">Dashboard</RouterLink>
          <RouterLink :to="companyPath('/deals')" class="nav-link" @click="mobileNavOpen = false">Deals</RouterLink>
          <RouterLink :to="companyPath('/proofs')" class="nav-link" @click="mobileNavOpen = false">Proofs</RouterLink>
          <RouterLink :to="companyPath('/accounts')" class="nav-link" @click="mobileNavOpen = false">Advertisers</RouterLink>
          <RouterLink :to="companyPath('/orders/new')" class="nav-link" @click="mobileNavOpen = false">New Order</RouterLink>
          <RouterLink :to="companyPath('/orders/book-package')" class="nav-link" @click="mobileNavOpen = false">Book Package</RouterLink>
        </div>

        <GlobalSearch />

        <div class="nav-spacer" />

        <div class="nav-user">
          <span>{{ repName }}</span>
          <button class="btn btn-ghost btn-sm" style="color: var(--nav-text);" @click="logout">
            Sign out
          </button>
          <details class="device-menu">
            <summary>Devices</summary>
            <div class="device-menu-content">
              <p v-if="authStore.remembered">Sign out releases your license and leaves this browser remembered.</p>
              <button v-if="authStore.remembered" class="btn btn-ghost btn-sm" @click="forgetDevice">Forget this device</button>
              <button class="btn btn-ghost btn-sm" @click="forgetOtherDevices">Forget other devices</button>
            </div>
          </details>
        </div>
      </div>
    </nav>

    <p v-if="authStore.notice && showNav" role="status" style="padding: 1rem;">{{ authStore.notice }}</p>
    <RouterView />
    <dialog ref="authenticationDialog" @cancel.prevent style="position: fixed; inset: 0; margin: auto; max-width: 460px; width: calc(100% - 2rem); max-height: 90dvh; overflow: auto; border: 0; border-radius: 12px; padding: 1rem; background: var(--bg-primary); color: var(--text-primary);">
      <LoginView v-if="authStore.pauseReason === 'verify' || authStore.pauseReason === 'license'" embedded />
      <template v-else>
        <h2>{{ authStore.pauseReason === 'ended' ? 'Signed out' : 'Session paused' }}</h2>
        <p>{{ authStore.error }}</p>
        <p>Your unfinished work remains on this page. Nothing will be saved automatically.</p>
        <a v-if="!authStore.remembered" :href="companyPath('/login') + '?returnTo=' + encodeURIComponent(route.fullPath)">Log In</a>
        <button v-if="authStore.pauseReason === 'retry'" class="btn btn-primary" :disabled="authStore.isLoading" @click="authStore.resume(true)">Retry sign-in</button>
        <p v-else>Returning to this tab from the background or reloading will attempt sign-in again if this browser is still remembered.</p>
        <button class="btn btn-ghost" @click="logout">Sign out and close this page</button>
      </template>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { companyPath, safeCompanyReturn } from '@/utils/companyNavigation'
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { getTheme, getConfig, getSessionTokenRef } from '@/api/config'
import { beaconLogout, keepSessionAlive } from '@/api/salesRepApi'
import GlobalSearch from '@/components/GlobalSearch.vue'
import LoginView from '@/views/LoginView.vue'
import { startSessionTabTracking, stopSessionTabTracking } from '@/utils/sessionTabs'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const mobileNavOpen = ref(false)
const sessionToken = getSessionTokenRef()
const authenticationDialog = ref<HTMLDialogElement>()
let leaseTimer: ReturnType<typeof setInterval> | undefined

const theme = computed(() => getTheme())
const portalName = computed(() => getConfig()?.portalName ?? 'SalesScout')
const repName = computed(() => authStore.repName)

const isAdminRoute = computed(() => !!route.meta.isAdmin)
const showNav = computed(() => authStore.isAuthenticated && !isAdminRoute.value)

async function logout() {
  await authStore.logout()
  router.push(companyPath('/login'))
}

async function forgetDevice() {
  if (!window.confirm('Forget this browser for this company? Your next sign-in will require an email code.')) return
  await authStore.forgetDevice()
  router.push(companyPath('/login'))
}

async function forgetOtherDevices() {
  if (window.confirm('Sign out and forget your other SalesScout devices for this company? This browser will stay signed in.')) await authStore.forgetOtherDevices()
}

async function visibilityChanged() {
  if (document.visibilityState !== 'visible') return
  await authStore.foreground()
  if (authStore.isAuthenticated && route.name === 'login') router.replace(safeCompanyReturn(route.query.returnTo) || companyPath('/dashboard'))
}

function pageReturned(event: PageTransitionEvent) {
  if (event.persisted) void visibilityChanged()
}

function activeUse(event: Event) { void authStore.activity(event) }

// Release the license seat only when the last live SalesScout tab closes.
// navigator.sendBeacon is fire-and-forget and works even during page unload.
function handleBeforeUnload() {
  if (authStore.isAuthenticated && sessionToken.value && !sessionToken.value.startsWith('sr2.')) {
    const isLastTab = stopSessionTabTracking(sessionToken.value)
    if (isLastTab) {
      beaconLogout(sessionToken.value)
    }
  }
}

onMounted(() => {
  // Apply theme immediately
  document.documentElement.setAttribute('data-theme', theme.value)
  window.addEventListener('beforeunload', handleBeforeUnload)
  document.addEventListener('visibilitychange', visibilityChanged)
  window.addEventListener('pageshow', pageReturned)
  window.addEventListener('pointerdown', activeUse, { passive: true })
  window.addEventListener('keydown', activeUse)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
  document.removeEventListener('visibilitychange', visibilityChanged)
  window.removeEventListener('pageshow', pageReturned)
  window.removeEventListener('pointerdown', activeUse)
  window.removeEventListener('keydown', activeUse)
  stopSessionTabTracking()
  clearInterval(leaseTimer)
})

watch(theme, (t) => {
  document.documentElement.setAttribute('data-theme', t)
})

watch(() => authStore.pauseReason, async reason => {
  await nextTick()
  const dialog = authenticationDialog.value
  if (reason && dialog && !dialog.open) dialog.showModal()
  else if (!reason && dialog?.open) dialog.close()
}, { immediate: true })

watch(
  [() => authStore.isAuthenticated, sessionToken],
  ([isAuthenticated, token]) => {
    clearInterval(leaseTimer)
    if (isAuthenticated && token) {
      if (!token.startsWith('sr2.')) startSessionTabTracking(token)
      else stopSessionTabTracking()
      keepSessionAlive()
      leaseTimer = setInterval(() => {
        if (!token.startsWith('sr2.') || document.visibilityState === 'visible') void keepSessionAlive()
      }, 20000)
      return
    }

    stopSessionTabTracking()
  },
  { immediate: true }
)
</script>

<style scoped>
.device-menu { position: relative; }
.device-menu summary { cursor: pointer; padding: .5rem; }
.device-menu-content { position: absolute; right: 0; top: 100%; z-index: 100; width: min(300px, calc(100vw - 2rem)); display: grid; gap: .5rem; padding: 1rem; border: 1px solid var(--border-color); border-radius: .5rem; background: var(--bg-primary); color: var(--text-primary); box-shadow: 0 4px 16px #0003; }
dialog::backdrop { background: #0008; }
</style>
